import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import AsyncSelect from 'react-select/async';
import moment from 'moment';

import { PATHS } from 'router/config';
import { useConversationsBrowser, IBrowserConversation } from 'core/hooks';
import {
  PaginatedStudentsDocument,
  PaginatedStudentsQuery,
  PaginatedStudentsQueryVariables,
} from 'core/graphql';

import * as S from './AccountManagerDashboard.styled';

const GMAIL_KIND = 'gmail_thread';

interface IStudentOption {
  value: string;
  label: string;
}

interface ICommunicationsSectionProps {
  /** Text search from the dashboard's global filter bar. */
  search: string;
  /** Group filter from the dashboard's global filter bar ('' = all). */
  groupId: string;
}

const studentName = (c: IBrowserConversation): string =>
  [c.student?.lastName, c.student?.firstName].filter(Boolean).join(' ');

/**
 * Dashboard communications browser (Slack workspace rework): every mirrored
 * conversation across all groups — group + text search come from the page's
 * global filter bar; student / date range / "waiting for a reply" are local.
 * A click deep-links into the student's or group's communication tab; group
 * rows also carry the conversation id (`&conv=`) so the feed opens
 * pre-filtered to that channel.
 */
export const CommunicationsSection = memo(
  ({ search, groupId }: ICommunicationsSectionProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const apollo = useApolloClient();

    const [student, setStudent] = useState<IStudentOption | null>(null);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [unansweredOnly, setUnansweredOnly] = useState(true);

    const hasFilters = Boolean(
      search.trim() || groupId || student || dateFrom || dateTo,
    );

    const {
      conversations,
      totalCount,
      hasNextPage,
      loadMore,
      loading,
      error,
    } = useConversationsBrowser({
      search: search.trim() || undefined,
      groupId: groupId || undefined,
      studentId: student?.value,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      unansweredOnly,
    });

    const loadStudentOptions = useCallback(
      async (input: string): Promise<IStudentOption[]> => {
        try {
          const result = await apollo.query<
            PaginatedStudentsQuery,
            PaginatedStudentsQueryVariables
          >({
            query: PaginatedStudentsDocument,
            variables: { query: input || '', first: 20 },
            fetchPolicy: 'no-cache',
          });
          const edges = result.data?.allStudents?.edges || [];
          return edges
            .map((edge) => edge?.node)
            .filter((node): node is NonNullable<typeof node> => Boolean(node))
            .map((node) => {
              const u = node.user;
              const name =
                [u?.firstName, u?.lastName].filter(Boolean).join(' ') ||
                u?.email ||
                node.id;
              return {
                value: node.id,
                label: `${name}${u?.email ? ` · ${u.email}` : ''}`,
              };
            });
        } catch {
          return [];
        }
      },
      [apollo],
    );

    const open = (c: IBrowserConversation) => {
      const grp = c.group || c.workspaceGroup;
      if (c.student) {
        history.push(
          `${PATHS.studentDetail.replace(
            ':id',
            c.student.id,
          )}?tab=communication`,
        );
      } else if (grp) {
        history.push(
          `${PATHS.groupDetail.replace(
            ':id',
            grp.id,
          )}?tab=communication&conv=${encodeURIComponent(c.id)}`,
        );
      }
    };

    return (
      <>
        <S.SectionTitle>
          {t('pages.dashboard.commsTitle')}
          {totalCount > 0 ? <S.BucketCount>{totalCount}</S.BucketCount> : null}
        </S.SectionTitle>
        <S.Card>
          <S.TaskBar>
            <S.StudentPickerBox>
              <AsyncSelect
                cacheOptions
                defaultOptions
                isClearable
                value={student}
                onChange={(v) => setStudent((v as IStudentOption) || null)}
                loadOptions={loadStudentOptions}
                placeholder={t('pages.dashboard.commsStudentPlaceholder')}
              />
            </S.StudentPickerBox>
            <S.Input
              type="date"
              value={dateFrom}
              title={t('pages.dashboard.commsDateFrom')}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <S.Input
              type="date"
              value={dateTo}
              title={t('pages.dashboard.commsDateTo')}
              onChange={(e) => setDateTo(e.target.value)}
            />
            <S.Toggle>
              <input
                type="checkbox"
                checked={unansweredOnly}
                onChange={(e) => setUnansweredOnly(e.target.checked)}
              />
              {t('pages.dashboard.commsOnlyUnanswered')}
            </S.Toggle>
          </S.TaskBar>

          {loading && conversations.length === 0 ? (
            <S.Muted>{t('pages.dashboard.loading')}</S.Muted>
          ) : error && conversations.length === 0 ? (
            // A failed query must not masquerade as "all answered".
            <S.Muted>{t('pages.dashboard.loadFailed')}</S.Muted>
          ) : conversations.length === 0 ? (
            unansweredOnly && !hasFilters ? (
              <S.AllAnswered>
                ✓ {t('pages.dashboard.allAnswered')}
              </S.AllAnswered>
            ) : (
              <S.Muted>{t('pages.dashboard.commsEmpty')}</S.Muted>
            )
          ) : (
            <S.UnansweredList>
              {conversations.map((c) => {
                const isGmail = c.kind === GMAIL_KIND;
                const tone = isGmail
                  ? ('email' as const)
                  : c.kind === 'slack_dm'
                  ? ('slack' as const)
                  : ('channel' as const);
                const label = isGmail
                  ? t('pages.communications.channelEmail')
                  : c.kind === 'slack_dm'
                  ? t('pages.communications.channelSlackDm')
                  : t('pages.communications.channelChannel');
                const groupName = c.group?.name || c.workspaceGroup?.name;
                // Rows without a student are group-channel threads — name the
                // group so the manager knows whose feed the row belongs to.
                const primary = c.student
                  ? studentName(c) || c.displayTitle || '—'
                  : [groupName, c.displayTitle].filter(Boolean).join(' · ') ||
                    '—';
                return (
                  <S.UnansweredRow
                    key={c.id}
                    type="button"
                    onClick={() => open(c)}
                  >
                    <S.ChannelBadge tone={tone}>{label}</S.ChannelBadge>
                    <S.UnansweredMain>
                      <b>{primary}</b>
                      {isGmail && c.mailboxEmail ? (
                        <span>
                          {t('pages.communications.mailboxTo')}:{' '}
                          {c.mailboxEmail}
                        </span>
                      ) : null}
                    </S.UnansweredMain>
                    {c.student && groupName ? (
                      <S.Chip>{groupName}</S.Chip>
                    ) : null}
                    {c.status === 'in_progress' ? (
                      <S.Chip tone="accent">
                        {t('pages.communications.status.in_progress')}
                      </S.Chip>
                    ) : null}
                    {c.unreadCount > 0 ? (
                      <S.UnreadBubble>
                        {c.unreadCount} {t('pages.communications.newShort')}
                      </S.UnreadBubble>
                    ) : null}
                    <S.UnansweredTime>
                      {c.lastMessageAt
                        ? moment(c.lastMessageAt).format('DD.MM HH:mm')
                        : '—'}
                    </S.UnansweredTime>
                  </S.UnansweredRow>
                );
              })}
            </S.UnansweredList>
          )}

          {hasNextPage ? (
            <S.MoreButton type="button" disabled={loading} onClick={loadMore}>
              {t('pages.communications.loadMore')}
            </S.MoreButton>
          ) : null}
        </S.Card>
      </>
    );
  },
);

CommunicationsSection.displayName = 'CommunicationsSection';
